
import { useState } from 'react';

const SubscriptionPlans = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showSignupForm, setShowSignupForm] = useState(false);

  const plans = [
    {
      id: 'individual',
      name: 'Individual Plan',
      price: 89,
      period: 'month',
      description: 'Perfect for single adults seeking comprehensive virtual care',
      features: [
        'Unlimited virtual consultations',
        '24-hour response guarantee',
        'Prescription management',
        'Health records access',
        'Basic lab coordination',
        'Email support'
      ],
      popular: false,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'family',
      name: 'Family Plan',
      price: 149,
      period: 'month',
      description: 'Comprehensive care for families up to 4 members',
      features: [
        'Everything in Individual Plan',
        'Coverage for up to 4 family members',
        'Priority scheduling for specialty services',
        '20% discount on lab work',
        'Family health dashboard',
        'Phone & email support'
      ],
      popular: true,
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 'premium',
      name: 'Premium Plan',
      price: 199,
      period: 'month',
      description: 'Enhanced healthcare experience with additional benefits',
      features: [
        'Everything in Family Plan',
        'Dedicated care coordinator',
        '30% discount on all treatments',
        'Specialist referral network',
        'Annual wellness planning',
        '24/7 phone support'
      ],
      popular: false,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
    setShowSignupForm(true);
  };

  const handleCloseForm = () => {
    setShowSignupForm(false);
    setSelectedPlan(null);
  };

  return (
    <section className="py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
            Choose Your <strong>Virtual Primary Care</strong> Plan
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Select the perfect subscription plan for your healthcare needs. All plans include our 24-hour response guarantee.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 ${
                plan.popular ? 'ring-4 ring-teal-500 ring-opacity-50' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}
              
              <div className={`bg-gradient-to-r ${plan.color} p-6 lg:p-8 text-white ${plan.popular ? 'pt-10 lg:pt-12' : ''}`}>
                <h3 className="text-xl lg:text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl lg:text-4xl font-bold">${plan.price}</span>
                  <span className="text-base lg:text-lg opacity-80 ml-2">/{plan.period}</span>
                </div>
                <p className="text-base lg:text-lg opacity-90">{plan.description}</p>
              </div>

              <div className="p-6 lg:p-8">
                <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-teal-500 text-lg lg:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm lg:text-base text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer bg-gradient-to-r ${plan.color} text-white hover:shadow-lg transform hover:-translate-y-1`}
                >
                  Select {plan.name}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-600 mb-4">
            All plans include a 30-day money-back guarantee. Cancel anytime.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <i className="ri-shield-check-line text-teal-500 mr-2"></i>
              HIPAA Compliant
            </div>
            <div className="flex items-center">
              <i className="ri-lock-line text-teal-500 mr-2"></i>
              Secure Payments
            </div>
            <div className="flex items-center">
              <i className="ri-customer-service-line text-teal-500 mr-2"></i>
              24/7 Support
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Signup Form Modal */}
      {showSignupForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 lg:p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Subscribe to {plans.find(p => p.id === selectedPlan)?.name}
                </h3>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer p-2"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form
                id="subscription-signup"
                data-readdy-form
                action="https://readdy.ai/api/form/d41ojo2btitriltepdm0"
                method="POST"
                className="space-y-4 lg:space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  
                  // Validate required fields
                  const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth'];
                  for (const field of requiredFields) {
                    if (!formData.get(field)) {
                      alert(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
                      return;
                    }
                  }

                  // Submit form
                  fetch('https://readdy.ai/api/form/d41ojo2btitriltepdm0', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any)
                  }).then(() => {
                    alert('Subscription request submitted successfully! We will contact you within 24 hours to complete your enrollment.');
                    handleCloseForm();
                  }).catch(() => {
                    alert('There was an error submitting your request. Please try again.');
                  });
                }}
              >
                <input type="hidden" name="selectedPlan" value={selectedPlan || ''} />
                <input type="hidden" name="planPrice" value={plans.find(p => p.id === selectedPlan)?.price || ''} />

                <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="grid lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-707 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-707 mb-2">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">
                    Insurance Provider
                  </label>
                  <input
                    type="text"
                    name="insurance"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Enter your insurance provider (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">
                    Current Health Concerns
                  </label>
                  <textarea
                    name="healthConcerns"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Please describe any current health concerns or conditions (optional, max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">
                    Preferred Contact Method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="contactMethod" value="email" className="mr-2" defaultChecked />
                      <span className="text-sm">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contactMethod" value="phone" className="mr-2" />
                      <span className="text-sm">Phone</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contactMethod" value="text" className="mr-2" />
                      <span className="text-sm">Text Message</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    required
                    className="mt-1 mr-3 flex-shrink-0"
                  />
                  <label className="text-sm text-gray-600">
                    I agree to the Terms of Service and Privacy Policy, and consent to receive communications about my healthcare and subscription. *
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="flex-1 py-3 px-6 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SubscriptionPlans;
